// Inline SVG marks and illustrations. Kept dependency-free (no icon library)
// so the kiosk bundle stays small. Everything inherits `currentColor` unless
// it needs a brand color of its own.

type IconProps = { size?: number; className?: string };

export function ArrowLeft({ size = 22, className }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15 5 L8 12 L15 19"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.5"
      />
    </svg>
  );
}

export function ChevronUp({ size = 28, className }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 15 L12 8 L19 15"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2.5"
      />
    </svg>
  );
}

export function CalendarGlyph({ size = 22, className }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        height="16"
        rx="0"
        stroke="currentColor"
        strokeWidth="2"
        width="18"
        x="3"
        y="5"
      />
      <path d="M3 10 H21" stroke="currentColor" strokeWidth="2" />
      <path
        d="M8 3 V7 M16 3 V7"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  );
}

export function CardChipGlyph({ size = 22, className }: IconProps) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      height={size}
      viewBox="0 0 24 24"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        height="14"
        stroke="currentColor"
        strokeWidth="2"
        width="20"
        x="2"
        y="5"
      />
      <path d="M2 10 H22" stroke="currentColor" strokeWidth="2" />
      <path
        d="M6 15 H11"
        stroke="currentColor"
        strokeLinecap="round"
        strokeWidth="2"
      />
    </svg>
  );
}

/** Contactless-card illustration. `active` adds the working bob; the waves
 * pulse continuously. */
export function ContactlessCard({
  size = 220,
  active = false,
}: {
  size?: number;
  active?: boolean;
}) {
  return (
    <svg
      aria-hidden="true"
      height={size}
      viewBox="0 0 240 200"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g
        className={active ? "card-work" : undefined}
        transform="rotate(-8 120 110)"
      >
        <rect
          fill="var(--color-accent-strong)"
          height="96"
          rx="0"
          width="150"
          x="28"
          y="62"
        />
        <rect
          fill="rgba(255,255,255,0.16)"
          height="14"
          width="150"
          x="28"
          y="78"
        />
        <rect
          fill="var(--color-accent)"
          height="20"
          width="26"
          x="44"
          y="104"
        />
        <rect
          fill="rgba(255,255,255,0.45)"
          height="6"
          width="80"
          x="44"
          y="136"
        />
      </g>
      <g
        fill="none"
        stroke="var(--color-accent)"
        strokeLinecap="round"
        strokeWidth="6"
      >
        <path className="wave-1" d="M188 78 a30 30 0 0 1 0 44" />
        <path className="wave-2" d="M198 66 a48 48 0 0 1 0 68" />
        <path className="wave-3" d="M208 54 a66 66 0 0 1 0 92" />
      </g>
    </svg>
  );
}

/** Thermal printer with a ticket sliding out. */
export function PrinterWithTicket({ size = 220 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      height={size}
      viewBox="0 0 240 220"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g className="ticket-out">
        <rect
          fill="var(--color-surface)"
          height="84"
          stroke="var(--color-border-strong)"
          strokeWidth="2"
          width="96"
          x="72"
          y="22"
        />
        <rect fill="var(--color-accent)" height="44" width="44" x="86" y="32" />
        <rect
          fill="var(--color-border-strong)"
          height="5"
          width="68"
          x="86"
          y="84"
        />
      </g>
      <rect
        fill="var(--color-accent-strong)"
        height="86"
        rx="0"
        width="160"
        x="40"
        y="110"
      />
      <rect fill="var(--color-cream)" height="10" width="120" x="60" y="118" />
      <rect
        fill="rgba(255,255,255,0.85)"
        height="6"
        width="40"
        x="150"
        y="146"
      />
      <rect fill="var(--color-accent)" height="6" width="40" x="150" y="160" />
    </svg>
  );
}

export function SuccessCheck({ size = 64 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height={size}
      viewBox="0 0 64 64"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className="check-path"
        d="M16 33 L27 44 L48 21"
        stroke="#fff"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="6"
      />
    </svg>
  );
}

export function BangMark({ size = 64 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height={size}
      viewBox="0 0 64 64"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M32 16 V36"
        stroke="#fff"
        strokeLinecap="round"
        strokeWidth="7"
      />
      <circle cx="32" cy="47" fill="#fff" r="4.5" />
    </svg>
  );
}

export function WrenchMark({ size = 64 }: { size?: number }) {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height={size}
      viewBox="0 0 64 64"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M40 14 a12 12 0 0 0 -14 16 L14 42 a4 4 0 0 0 0 6 l2 2 a4 4 0 0 0 6 0 l12 -12 a12 12 0 0 0 16 -14 l-8 8 -8 -8 z"
        stroke="#fff"
        strokeLinejoin="round"
        strokeWidth="4"
      />
    </svg>
  );
}
